import { repositoryFactory } from '@data';
import { startStudySession, beginQuestionsAfterResume } from '@core/algorithms';
import { decayCalculator } from './index.js';

/**
 * Session Manager Service
 * Handles study session lifecycle and warm-up selection
 */
export class SessionManager {
  constructor() {
    this.courseRepository = repositoryFactory.getCourseRepository();
    this.progressRepository = repositoryFactory.getProgressRepository();
    this.sessionRepository = repositoryFactory.getSessionRepository();
  }

  /**
   * Start a new study session with warm-up questions
   * Applies score decay to all questions before starting
   */
  async startSession(userId, courseId, sectionId) {
    // Get course and progress
    const course = await this.courseRepository.getCourseById(courseId);
    const progress = await this.progressRepository.getUserProgress(userId, courseId);

    if (!course || !progress) {
      throw new Error('Course or progress not found');
    }

    // Check for existing active session
    const activeSession = await this.sessionRepository.getActiveSession(userId, courseId);
    if (activeSession) {
      return activeSession;
    }

    // STEP 1: Apply decay to all questions in the course
    // This models the forgetting curve and triggers reinforcement need detection
    console.log('[SessionManager.startSession] Applying score decay...');
    try {
      const decayResults = await decayCalculator.applyDecayToAllQuestions(userId, courseId);
      console.log('[SessionManager.startSession] Decay applied to', decayResults.questionsDecayed, 'questions');
      
      // Reload progress after decay is applied
      await this.progressRepository.updateUserProgress(progress);
    } catch (err) {
      console.error('[SessionManager.startSession] Error applying decay:', err);
      // Don't fail the session start on decay error
    }

    // STEP 2: Get current section
    const currentSection = course.getSectionById(sectionId);
    if (!currentSection) {
      throw new Error(`Section not found: ${sectionId}`);
    }

    // STEP 3: Prepare progress data for resume algorithm
    // Include decayed amounts for decay-triggered reinforcement
    const progressForAlgorithm = {
      sections: course.sections.map(section => {
        const sectionProg = progress.getSectionProgress(section.id) || { score: 0 };
        return {
          sectionId: section.id,
          title: section.title,
          index: section.index,
          score: sectionProg.score || 0,
          lastStudiedAt: sectionProg.lastActivityAt || null,
          decayedAmount: sectionProg.decayedAmount || 0,  // Track decay for reinforcement
        };
      }),
      lastStudiedSection: progress.currentSectionId || course.sections[0]?.id,
    };

    // Use algorithm to determine resume state
    const resumeState = startStudySession(progressForAlgorithm);

    // STEP 4: Create session
    const session = await this.sessionRepository.createSession(userId, courseId, sectionId);
    session.resumeState = resumeState;
    await this.sessionRepository.updateSession(session);

    console.log('[SessionManager.startSession] Session started for section:', sectionId);
    return session;
  }

  /**
   * Resume an existing session
   */
  async resumeSession(userId, courseId) {
    const session = await this.sessionRepository.getActiveSession(userId, courseId);
    
    if (!session) {
      return null;
    }

    return session;
  }

  /**
   * Get warm-up questions after resuming
   */
  async getResumeWarmUpQuestions(userId, courseId, currentSectionId) {
    const course = await this.courseRepository.getCourseById(courseId);
    const progress = await this.progressRepository.getUserProgress(userId, courseId);

    if (!course || !progress) {
      return [];
    }

    // Build question pool
    const questionPool = [];
    
    for (const section of course.sections) {
      if (!progress.isSectionUnlocked(section.id)) {
        continue;
      }

      for (const question of section.questions) {
        const questionProgress = progress.getQuestionProgress(question.id) || {
          score: 0,
          attempts: 0,
          lastAttemptAt: null,
          lastDecayAppliedAt: null,
        };

        questionPool.push({
          questionId: question.id,
          sectionId: section.id,
          progress: questionProgress,
        });
      }
    }

    // Use algorithm to select questions
    const warmUpQuestions = beginQuestionsAfterResume(questionPool, currentSectionId);

    return warmUpQuestions;
  }

  /**
   * Record answer in session
   */
  async recordSessionAnswer(sessionId, questionId, isCorrect, timeSpent, hintsUsed) {
    const session = await this.sessionRepository.getSessionById(sessionId);
    
    if (!session) {
      throw new Error(`Session not found: ${sessionId}`);
    }

    // Add answered question to session
    session.addAnsweredQuestion(questionId, isCorrect, timeSpent, hintsUsed);
    await this.sessionRepository.updateSession(session);

    return session;
  }

  /**
   * Complete a session
   */
  async completeSession(sessionId) {
    const session = await this.sessionRepository.completeSession(sessionId);
    return session;
  }

  /**
   * Get session statistics
   */
  async getSessionStats(sessionId) {
    const session = await this.sessionRepository.getSessionById(sessionId);
    
    if (!session) {
      return null;
    }

    return {
      id: session.id,
      duration: session.getDuration(),
      accuracy: session.getAccuracy(),
      questionsAnswered: session.performanceMetrics.totalQuestions,
      correctAnswers: session.performanceMetrics.correctAnswers,
      hintsUsed: session.performanceMetrics.hintsUsed,
      averageTime: session.performanceMetrics.averageTime,
      struggledQuestions: session.performanceMetrics.struggledQuestions,
      isComplete: session.isComplete,
    };
  }

  /**
   * Get session history
   */
  async getSessionHistory(userId, courseId, limit = 10) {
    const sessions = await this.sessionRepository.getSessionHistory(userId, courseId, limit);
    
    return sessions.map(session => ({
      id: session.id,
      sectionId: session.sectionId,
      startTime: session.startTime,
      endTime: session.endTime,
      duration: session.getDuration(),
      accuracy: session.getAccuracy(),
      questionsAnswered: session.performanceMetrics.totalQuestions,
      isComplete: session.isComplete,
    }));
  }
}

// Export singleton instance
export const sessionManager = new SessionManager();
